import { CheckCircle2, Download, FileUp, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { telechargerExportBareme, telechargerModeleBareme, useActionsPaie, useCorrespondancesGrades, useImpotBaremes, useVersionBaremePaie, useVersionsBaremePaie } from '@/api/hooks';
import type { ImportBaremeRapport, TrancheImpotSaisie } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { StatutVersionBadge } from '@/components/paie';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { dateFr, dateHeureFr, montant, nombre } from '@/lib/utils';

const message = (e: unknown) => (e instanceof Error ? e.message : 'Erreur inattendue.');
const usd = (n: number | null | undefined) => montant(n, true);
const ONGLETS = ['versions', 'correspondances', 'impot'] as const;
type Onglet = (typeof ONGLETS)[number];
const LIBELLE_ONGLET: Record<Onglet, string> = { versions: 'Barème', correspondances: 'Correspondances des grades', impot: 'Impôt sur les rémunérations' };

/**
 * Barème des rémunérations (spécifications HRMS et Paie, § 5.3, § 7.2 ; lot HP-2 ; décisions D7, D7 bis, D9 à D11 bis,
 * D27 bis) : versions, lignes par grade, importation de la feuille du barème, approbation (DF, DG), correspondances des
 * grades, barèmes de l'impôt par période. Les montants ne sont visibles que des profils de la paie.
 */
export function BaremePaie() {
  const [params, setParams] = useSearchParams();
  const onglet: Onglet = ONGLETS.includes(params.get('onglet') as Onglet) ? (params.get('onglet') as Onglet) : 'versions';
  const changer = (o: string) => {
    const n = new URLSearchParams(params);
    n.set('onglet', o);
    setParams(n, { replace: true });
  };
  return (
    <div className="space-y-6">
      <PageHeader titre="Barème des rémunérations" sousTitre="Versions datées du barème par grade (brut, prime, logement, transport), correspondances des grades du référentiel, barème de l'impôt par période ; approbation par le Directeur financier ou le Directeur Général." />
      <Tabs value={onglet} onValueChange={changer}>
        <TabsList className="flex-wrap">
          {ONGLETS.map((o) => (
            <TabsTrigger key={o} value={o}>
              {LIBELLE_ONGLET[o]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {onglet === 'versions' && <Versions versionId={params.get('version')} choisir={(id) => { const n = new URLSearchParams(params); n.set('version', id); setParams(n, { replace: true }); }} />}
      {onglet === 'correspondances' && <Correspondances />}
      {onglet === 'impot' && <Impot />}
    </div>
  );
}

function Versions({ versionId, choisir }: { versionId: string | null; choisir: (id: string) => void }) {
  const u = useSession()!.utilisateur;
  const gestion = u.profils.some((p) => ['DIRECTEUR_RH', 'RESSOURCES_HUMAINES', 'PAIE', 'DIRECTEUR_FINANCIER'].includes(p));
  const versions = useVersionsBaremePaie();
  const courante = versionId ?? versions.data?.find((v) => v.statut === 'EN_VIGUEUR')?.id ?? versions.data?.[0]?.id;
  const detail = useVersionBaremePaie(courante);
  const actions = useActionsPaie();
  const [creation, setCreation] = useState(false);
  const [importer, setImporter] = useState(false);
  const [edition, setEdition] = useState(false);
  useEffect(() => {
    if (!versionId && courante) choisir(courante);
  }, [versionId, courante, choisir]);
  if (versions.isLoading) return <Chargement />;
  if (versions.error) return <ErreurBloc erreur={versions.error} />;
  const v = detail.data?.version;
  const agir = async (p: Promise<unknown>, ok: string) => {
    try {
      await p;
      toast.success(ok);
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <div className="grid min-w-0 gap-6 lg:grid-cols-3">
      <Card className="min-w-0 lg:col-span-1">
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center justify-between gap-2">
            Versions
            {gestion && (
              <Button size="sm" onClick={() => setCreation(true)}>
                <Plus className="size-4" /> Nouvelle version
              </Button>
            )}
          </CardTitle>
          <CardDescription>Une seule version en vigueur à une date donnée ; la précédente est archivée à la veille de la nouvelle date d'effet.</CardDescription>
        </CardHeader>
        <CardContent>
          {!versions.data?.length ? (
            <Vide message="Aucune version : créez la version 1 (en vigueur depuis la création du FONAREV, D7 bis)." />
          ) : (
            <ul className="divide-y text-sm" data-testid="versions-bareme">
              {versions.data.map((x) => (
                <li key={x.id} className={`cursor-pointer py-2 ${x.id === courante ? 'font-medium' : ''}`} onClick={() => choisir(x.id)}>
                  <div className="flex items-center justify-between gap-2">
                    <span>Version {x.numero} · {x.libelle}</span>
                    <StatutVersionBadge statut={x.statut} />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    effet du {dateFr(x.debut)}{x.fin ? ` au ${dateFr(x.fin)}` : ''} · {x.lignes} grade(s){x.approuvePar ? ` · approuvée par ${x.approuvePar}` : ''}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <Card className="min-w-0 lg:col-span-2">
        {detail.isLoading ? (
          <CardContent className="pt-6"><Chargement /></CardContent>
        ) : !v || !detail.data ? (
          <CardContent className="pt-6"><Vide message="Sélectionnez une version." /></CardContent>
        ) : (
          <>
            <CardHeader>
              <CardTitle className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  Version {v.numero} · {v.libelle} <StatutVersionBadge statut={v.statut} />
                </span>
                <span className="flex flex-wrap gap-2">
                  {gestion && (
                    <Button size="sm" variant="outline" onClick={() => setEdition(true)}>
                      Règles et dates
                    </Button>
                  )}
                  {detail.data.droits.modifier && (
                    <Button size="sm" variant="outline" onClick={() => setImporter(true)} data-testid="importer-bareme">
                      <FileUp className="size-4" /> Importer la feuille
                    </Button>
                  )}
                  {detail.data.droits.approuver && (
                    <Button size="sm" onClick={() => agir(actions.approuverVersion.mutateAsync({ id: v.id }), 'Version approuvée : en vigueur, fiches recalculées.')} disabled={actions.approuverVersion.isPending} data-testid="approuver-bareme">
                      <CheckCircle2 className="size-4" /> Approuver
                    </Button>
                  )}
                  {detail.data.droits.archiver && v.statut === 'EN_VIGUEUR' && (
                    <Button size="sm" variant="ghost" onClick={() => agir(actions.archiverVersion.mutateAsync(v.id), 'Version archivée.')}>
                      Archiver
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => telechargerExportBareme(v.id, v.numero).catch((e) => toast.error(message(e)))}>
                    <Download className="size-4" /> Excel
                  </Button>
                </span>
              </CardTitle>
              <CardDescription>
                Effet du {dateFr(v.debut)}{v.fin ? ` au ${dateFr(v.fin)}` : ''} · CNSS salariale {nombre(v.tauxCnssSalarial, 2)} % · logement {nombre(v.logementPctBrut, 0)} % du brut (sauf valeur de la ligne) · prime du grade DIRECTEUR {v.primeDirecteurImposable ? 'imposable et cotisable' : 'exonérée'} · taux de change {v.tauxChangeCdfUsd ? `${nombre(v.tauxChangeCdfUsd, 0)} CDF/USD` : 'à renseigner'}
                {v.source ? ` · source : ${v.source}` : ''}
                {v.approuvePar ? ` · approuvée par ${v.approuvePar} le ${dateHeureFr(v.approuveLe)}` : ''}
                {v.commentaire ? ` · ${v.commentaire}` : ''}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!detail.data.lignes.length ? (
                <Vide message="Aucune ligne : importez la feuille du barème (modèle disponible) ou saisissez les lignes." />
              ) : (
                <div className="overflow-auto">
                  <Table data-testid="lignes-bareme">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Catégorie · échelon</TableHead>
                        <TableHead>Grade</TableHead>
                        <TableHead>Cat. 360</TableHead>
                        <TableHead className="text-right">Brut</TableHead>
                        <TableHead className="text-right">Prime</TableHead>
                        <TableHead className="text-right">Logement</TableHead>
                        <TableHead className="text-right">Transport</TableHead>
                        <TableHead className="text-right">Agents</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.data.lignes.map((l) => (
                        <TableRow key={l.id}>
                          <TableCell className="text-xs text-muted-foreground">
                            {l.categorie ?? '—'}
                            {l.echelon ? ` · ${l.echelon}` : ''}
                          </TableCell>
                          <TableCell>
                            {l.grade}
                            {l.cadreDirection && <Badge variant="outline" className="ml-2 text-[10px]">cadre de direction</Badge>}
                            {l.rubriques.length > 0 && <div className="text-xs text-muted-foreground">{l.rubriques.map((r) => r.libelle).join(', ')}</div>}
                          </TableCell>
                          <TableCell>{l.categorieAgent ?? '—'}</TableCell>
                          <TableCell className="text-right">{usd(l.brut)}</TableCell>
                          <TableCell className="text-right">{l.prime ? usd(l.prime) : '—'}</TableCell>
                          <TableCell className="text-right">
                            {usd(l.logement)}
                            {l.logementRegle && <span className="text-xs text-muted-foreground"> (règle)</span>}
                          </TableCell>
                          <TableCell className="text-right">{usd(l.transport)}</TableCell>
                          <TableCell className="text-right">{l.agents}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </>
        )}
      </Card>
      {creation && <CreationDialog versions={versions.data ?? []} onClose={() => setCreation(false)} onCree={choisir} />}
      {importer && v && <ImportDialog versionId={v.id} onClose={() => setImporter(false)} />}
      {edition && v && <EditionDialog version={v} onClose={() => setEdition(false)} />}
    </div>
  );
}

function CreationDialog({ versions, onClose, onCree }: { versions: Array<{ id: string; numero: number; libelle: string }>; onClose: () => void; onCree: (id: string) => void }) {
  const actions = useActionsPaie();
  const [s, setS] = useState({ libelle: '', debut: '', copierDe: '', tauxChangeCdfUsd: '', commentaire: '' });
  const valider = async () => {
    try {
      const d = await actions.creerVersion.mutateAsync({ libelle: s.libelle, debut: s.debut, copierDe: s.copierDe || undefined, tauxChangeCdfUsd: s.tauxChangeCdfUsd ? Number(s.tauxChangeCdfUsd) : null, commentaire: s.commentaire || null });
      toast.success(`Version ${d.version.numero} créée en préparation.`);
      onCree(d.version.id);
      onClose();
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dialogue-version-bareme">
        <DialogHeader>
          <DialogTitle>Nouvelle version du barème</DialogTitle>
          <DialogDescription>Créée en préparation ; importez la feuille du barème puis faites approuver la version par le Directeur financier ou le Directeur Général.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1"><Label htmlFor="vb-libelle">Libellé *</Label><Input id="vb-libelle" value={s.libelle} placeholder="Barème version 1 (création du FONAREV)" onChange={(e) => setS({ ...s, libelle: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="vb-debut">Date d'effet *</Label><Input id="vb-debut" type="date" value={s.debut} onChange={(e) => setS({ ...s, debut: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="vb-copie">Copier les lignes de</Label><SelectNatif id="vb-copie" value={s.copierDe} onChange={(e) => setS({ ...s, copierDe: e.target.value })}><option value="">Aucune (version vide)</option>{versions.map((v) => <option key={v.id} value={v.id}>Version {v.numero} · {v.libelle}</option>)}</SelectNatif></div>
          <div className="space-y-1"><Label htmlFor="vb-taux">Taux de change CDF pour 1 USD (impôt indicatif)</Label><Input id="vb-taux" type="number" min="1" value={s.tauxChangeCdfUsd} onChange={(e) => setS({ ...s, tauxChangeCdfUsd: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="vb-com">Commentaire</Label><Input id="vb-com" value={s.commentaire} onChange={(e) => setS({ ...s, commentaire: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={valider} disabled={!s.libelle || !s.debut || actions.creerVersion.isPending}>Créer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditionDialog({ version, onClose }: { version: { id: string; statut: string; libelle: string; debut: string; fin: string | null; tauxCnssSalarial: number; logementPctBrut: number; primeDirecteurImposable: boolean; tauxChangeCdfUsd: number | null; commentaire: string | null }; onClose: () => void }) {
  const actions = useActionsPaie();
  const preparation = version.statut === 'PREPARATION';
  const [s, setS] = useState({ libelle: version.libelle, debut: version.debut, fin: version.fin ?? '', tauxCnssSalarial: String(version.tauxCnssSalarial), logementPctBrut: String(version.logementPctBrut), primeDirecteurImposable: version.primeDirecteurImposable, tauxChangeCdfUsd: version.tauxChangeCdfUsd === null ? '' : String(version.tauxChangeCdfUsd), commentaire: version.commentaire ?? '' });
  const valider = async () => {
    try {
      await actions.modifierVersion.mutateAsync({ id: version.id, corps: { libelle: s.libelle, fin: s.fin || null, tauxChangeCdfUsd: s.tauxChangeCdfUsd ? Number(s.tauxChangeCdfUsd) : null, commentaire: s.commentaire || null, ...(preparation ? { debut: s.debut, tauxCnssSalarial: Number(s.tauxCnssSalarial), logementPctBrut: Number(s.logementPctBrut), primeDirecteurImposable: s.primeDirecteurImposable } : {}) } });
      toast.success('Version mise à jour.');
      onClose();
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Règles et dates de la version</DialogTitle>
          <DialogDescription>{preparation ? 'Version en préparation : toutes les règles sont modifiables.' : 'Version en vigueur : seuls le libellé, la fin de validité, le taux de change et le commentaire changent ; les règles exigent une nouvelle version.'}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2"><Label htmlFor="ed-libelle">Libellé</Label><Input id="ed-libelle" value={s.libelle} onChange={(e) => setS({ ...s, libelle: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="ed-debut">Date d'effet</Label><Input id="ed-debut" type="date" value={s.debut} disabled={!preparation} onChange={(e) => setS({ ...s, debut: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="ed-fin">Fin de validité</Label><Input id="ed-fin" type="date" value={s.fin} onChange={(e) => setS({ ...s, fin: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="ed-cnss">CNSS salariale (%)</Label><Input id="ed-cnss" type="number" step="0.01" value={s.tauxCnssSalarial} disabled={!preparation} onChange={(e) => setS({ ...s, tauxCnssSalarial: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="ed-log">Logement (% du brut)</Label><Input id="ed-log" type="number" step="0.01" value={s.logementPctBrut} disabled={!preparation} onChange={(e) => setS({ ...s, logementPctBrut: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="ed-taux">Taux de change CDF/USD</Label><Input id="ed-taux" type="number" min="1" value={s.tauxChangeCdfUsd} onChange={(e) => setS({ ...s, tauxChangeCdfUsd: e.target.value })} /></div>
          <label className="flex items-center gap-2 self-end text-sm"><Checkbox checked={s.primeDirecteurImposable} disabled={!preparation} onCheckedChange={(v) => setS({ ...s, primeDirecteurImposable: v === true })} /> Prime du grade DIRECTEUR imposable et cotisable (D11 bis)</label>
          <div className="space-y-1 sm:col-span-2"><Label htmlFor="ed-com">Commentaire</Label><Input id="ed-com" value={s.commentaire} onChange={(e) => setS({ ...s, commentaire: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={valider} disabled={actions.modifierVersion.isPending}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ versionId, onClose }: { versionId: string; onClose: () => void }) {
  const actions = useActionsPaie();
  const [fichier, setFichier] = useState<File | null>(null);
  const [rapport, setRapport] = useState<ImportBaremeRapport | null>(null);
  const importer = async (simulation: boolean) => {
    if (!fichier) return;
    try {
      const r = await actions.importerBareme.mutateAsync({ id: versionId, fichier, simulation });
      setRapport(r);
      if (!simulation) toast.success(`${r.lignes} ligne(s) importée(s).`);
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="dialogue-import-bareme">
        <DialogHeader>
          <DialogTitle>Importer la feuille du barème</DialogTitle>
          <DialogDescription>Classeur au format du barème du Fonds (feuille 1, en-tête « GRADE FONCTIONS » et « Brut », taux CNSS en L7) : contrôlez d'abord, puis écrivez. Les colonnes calculées (traitement brut, CNSS, net imposable, IPR, net payé) ne sont pas reprises : FONAREV 360 les recalcule.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="ib-fichier">Classeur (.xlsx)</Label>
            <Input id="ib-fichier" type="file" accept=".xlsx" onChange={(e) => setFichier(e.target.files?.[0] ?? null)} data-testid="champ-import-bareme" />
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => telechargerModeleBareme().catch((e) => toast.error(message(e)))}>
            Modèle
          </Button>
          <Button type="button" variant="outline" size="sm" disabled={!fichier || actions.importerBareme.isPending} onClick={() => importer(true)}>
            Contrôler
          </Button>
          <Button type="button" size="sm" disabled={!fichier || actions.importerBareme.isPending} onClick={() => importer(false)}>
            Écrire
          </Button>
        </div>
        {rapport && (
          <div className="space-y-2 text-sm">
            <div className="font-medium">
              {rapport.simulation ? 'Contrôle' : 'Écriture'} : {rapport.lignes} grade(s){rapport.tauxCnss !== null ? ` · CNSS ${nombre(rapport.tauxCnss, 2)} %` : ''}
            </div>
            {rapport.anomalies.length > 0 && (
              <ul className="list-inside list-disc text-amber-800">
                {rapport.anomalies.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            )}
            <div className="max-h-64 overflow-auto rounded border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Grade</TableHead>
                    <TableHead>Catégorie 360</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rapport.grades.map((g) => (
                    <TableRow key={g.grade}>
                      <TableCell>{g.grade}</TableCell>
                      <TableCell>
                        {g.categorie}
                        {g.cadreDirection ? ' · cadre de direction' : ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Correspondances() {
  const { data, isLoading, error } = useCorrespondancesGrades();
  const actions = useActionsPaie();
  const u = useSession()!.utilisateur;
  const gestion = u.profils.some((p) => ['DIRECTEUR_RH', 'RESSOURCES_HUMAINES', 'PAIE', 'DIRECTEUR_FINANCIER'].includes(p));
  const [choix, setChoix] = useState<Record<string, string>>({});
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const enregistrer = async () => {
    try {
      await actions.correspondances.mutateAsync({ correspondances: Object.entries(choix).map(([grade, gradeBareme]) => ({ grade, gradeBareme })) });
      toast.success('Correspondances enregistrées : fiches recalculées.');
      setChoix({});
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Card data-testid="correspondances-grades">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          Grades du référentiel et lignes du barème
          {gestion && Object.keys(choix).length > 0 && (
            <Button size="sm" onClick={enregistrer} disabled={actions.correspondances.isPending}>
              Enregistrer ({Object.keys(choix).length})
            </Button>
          )}
        </CardTitle>
        <CardDescription>
          {data.version ? `Version ${data.version.numero} en vigueur · ` : 'Aucune version en vigueur · '}un grade dont le libellé diffère de celui du barème reçoit une correspondance ; les grades sans ligne bloquent la fiche de leurs agents (D5).
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!data.grades.length ? (
          <Vide message="Aucun grade dans le référentiel." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Grade (référentiel)</TableHead>
                <TableHead>Agents</TableHead>
                <TableHead>Ligne du barème</TableHead>
                {gestion && <TableHead>Correspondance</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.grades.map((g) => (
                <TableRow key={g.grade}>
                  <TableCell>
                    {g.grade}
                    <div className="text-xs text-muted-foreground">{g.categories.join(', ')}</div>
                  </TableCell>
                  <TableCell>{g.agents}</TableCell>
                  <TableCell>{g.ligne ? g.ligne.grade : <span className="text-destructive">aucune ligne</span>}{g.correspondance ? <span className="text-xs text-muted-foreground"> · correspondance déclarée</span> : null}</TableCell>
                  {gestion && (
                    <TableCell>
                      <SelectNatif value={choix[g.grade] ?? g.correspondance ?? ''} onChange={(e) => setChoix({ ...choix, [g.grade]: e.target.value })}>
                        <option value="">Libellé identique</option>
                        {data.lignes.map((l) => (
                          <option key={l.id} value={l.grade}>
                            {l.grade}
                          </option>
                        ))}
                      </SelectNatif>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function Impot() {
  const { data, isLoading, error } = useImpotBaremes();
  const actions = useActionsPaie();
  const u = useSession()!.utilisateur;
  const gestion = u.profils.some((p) => ['DIRECTEUR_RH', 'RESSOURCES_HUMAINES', 'PAIE', 'DIRECTEUR_FINANCIER'].includes(p));
  const approbation = u.profils.some((p) => ['DIRECTEUR_FINANCIER', 'DIRECTEUR_GENERAL'].includes(p));
  const [creation, setCreation] = useState(false);
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  return (
    <Card data-testid="impot-baremes">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          Barème de l'impôt sur les rémunérations
          {gestion && (
            <Button size="sm" onClick={() => setCreation(true)}>
              <Plus className="size-4" /> Nouveau barème
            </Button>
          )}
        </CardTitle>
        <CardDescription>Fixé par la loi (D27 bis) : tranches en francs congolais et plafond, renseignés par la Direction financière pour chaque période et approuvés ; aucun taux n'est inventé par l'application. Sert au net indicatif des fiches de référence puis à la paie.</CardDescription>
      </CardHeader>
      <CardContent>
        {!data.length ? (
          <Vide message="Aucun barème d'impôt : le net indicatif des fiches n'est pas calculé." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Barème</TableHead>
                <TableHead>Période</TableHead>
                <TableHead>Tranches</TableHead>
                <TableHead>Plafond</TableHead>
                <TableHead>Statut</TableHead>
                {approbation && <TableHead />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((i) => (
                <TableRow key={i.id}>
                  <TableCell>
                    {i.libelle}
                    {i.source ? <div className="text-xs text-muted-foreground">{i.source}</div> : null}
                  </TableCell>
                  <TableCell>
                    du {dateFr(i.debut)}
                    {i.fin ? ` au ${dateFr(i.fin)}` : ''}
                  </TableCell>
                  <TableCell className="text-xs">{i.tranches.map((t) => `${nombre(t.taux, 0)} %${t.plafondCdf !== null ? ` jusqu'à ${nombre(t.plafondCdf, 0)} CDF` : ' au-delà'}`).join(' · ')}</TableCell>
                  <TableCell>{nombre(i.plafondPct, 0)} %</TableCell>
                  <TableCell>
                    <StatutVersionBadge statut={i.statut} />
                    {i.approuvePar ? <div className="text-xs text-muted-foreground">par {i.approuvePar}</div> : null}
                  </TableCell>
                  {approbation && (
                    <TableCell className="text-right">
                      {i.statut === 'PREPARATION' && (
                        <Button size="sm" variant="outline" onClick={() => actions.approuverImpot.mutateAsync(i.id).then(() => toast.success('Barème d’impôt approuvé.')).catch((e) => toast.error(message(e)))}>
                          <CheckCircle2 className="size-4" /> Approuver
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      {creation && <ImpotDialog onClose={() => setCreation(false)} />}
    </Card>
  );
}

function ImpotDialog({ onClose }: { onClose: () => void }) {
  const actions = useActionsPaie();
  const [s, setS] = useState({ libelle: '', debut: '', fin: '', plafondPct: '30', source: '' });
  const [tranches, setTranches] = useState<Array<{ plafondCdf: string; taux: string }>>([{ plafondCdf: '', taux: '' }]);
  const valider = async () => {
    try {
      const t: TrancheImpotSaisie[] = tranches.map((x) => ({ plafondCdf: x.plafondCdf ? Number(x.plafondCdf) : null, taux: Number(x.taux) }));
      await actions.creerImpot.mutateAsync({ libelle: s.libelle, debut: s.debut, fin: s.fin || null, tranches: t, plafondPct: Number(s.plafondPct) || 30, source: s.source || null });
      toast.success('Barème d’impôt préparé : approbation du Directeur financier ou du Directeur Général attendue.');
      onClose();
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="dialogue-impot">
        <DialogHeader>
          <DialogTitle>Barème de l'impôt sur les rémunérations</DialogTitle>
          <DialogDescription>Tranches progressives en CDF (borne supérieure et taux ; la dernière sans borne) et plafond en pourcentage de la base imposable, d'après le texte de loi en vigueur.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2"><Label htmlFor="im-libelle">Libellé *</Label><Input id="im-libelle" value={s.libelle} onChange={(e) => setS({ ...s, libelle: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="im-debut">Début *</Label><Input id="im-debut" type="date" value={s.debut} onChange={(e) => setS({ ...s, debut: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="im-fin">Fin</Label><Input id="im-fin" type="date" value={s.fin} onChange={(e) => setS({ ...s, fin: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="im-plafond">Plafond (% de la base)</Label><Input id="im-plafond" type="number" step="0.01" value={s.plafondPct} onChange={(e) => setS({ ...s, plafondPct: e.target.value })} /></div>
          <div className="space-y-1"><Label htmlFor="im-source">Texte de référence</Label><Input id="im-source" value={s.source} onChange={(e) => setS({ ...s, source: e.target.value })} /></div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Tranches</Label>
            {tranches.map((t, i) => (
              <div key={i} className="grid grid-cols-2 gap-2">
                <Input type="number" min="0" placeholder={i === tranches.length - 1 ? 'au-delà (vide)' : 'borne supérieure (CDF)'} value={t.plafondCdf} onChange={(e) => setTranches(tranches.map((x, k) => (k === i ? { ...x, plafondCdf: e.target.value } : x)))} />
                <Input type="number" step="0.01" min="0" max="100" placeholder="taux (%)" value={t.taux} onChange={(e) => setTranches(tranches.map((x, k) => (k === i ? { ...x, taux: e.target.value } : x)))} />
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" onClick={() => setTranches([...tranches, { plafondCdf: '', taux: '' }])}>
              <Plus className="size-4" /> Ajouter une tranche
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={valider} disabled={!s.libelle || !s.debut || tranches.some((t) => t.taux === '') || actions.creerImpot.isPending}>Préparer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
